// Role preview mode (fully offline).
// When the app is loaded inside the role-preview frame (Settings > Manage Roles > Create a role >
// Review > Preview The Platform), this module runs before anything else and turns the app into a
// read-only copy of itself that never touches the network:
//   - every axios request and every fetch is answered in the browser from fixtures (./fixtures);
//     nothing is sent to any server, so nothing can be read from or written to the account;
//   - WebSocket, EventSource and sendBeacon are inert;
//   - localStorage, sessionStorage and cookie writes stay inside the frame;
//   - analytics calls are swallowed;
//   - the app's own left and right navs are hidden (the preview draws its own nav).
// Outside the preview frame this module does nothing.

import axios from 'axios';
import { PREVIEW_FRAME_NAME, PREVIEW_PROFILE_ID, PREVIEW_PROFILE_NAME } from './constants';
import history from '../history';
import { fixtureFor } from './fixtures/index';
import { translationFor } from './fixtures/translations';
import { settingsPageVisible } from './fixtures/shell';
import { settingsRoutes } from '../components/ProfileLeftNavBar/helper';

export const isPreviewMode = () =>
	typeof window !== 'undefined' && window.name === PREVIEW_FRAME_NAME && window.parent !== window;

// In-memory Storage clone seeded from the real one. Supports getItem/setItem and property access.
const shadowStorage = (real) => {
	const store = new Map();
	try {
		for (let i = 0; i < real.length; i++) {
			const k = real.key(i);
			store.set(k, real.getItem(k));
		}
	} catch (e) { /* storage blocked: start empty */ }
	const api = {
		getItem: (k) => (store.has(String(k)) ? store.get(String(k)) : null),
		setItem: (k, v) => { store.set(String(k), String(v)); },
		removeItem: (k) => { store.delete(String(k)); },
		clear: () => { store.clear(); },
		key: (i) => { const keys = Array.from(store.keys()); return i < keys.length ? keys[i] : null; },
	};
	return new Proxy(api, {
		get: (t, p) => {
			if (p === 'length') return store.size;
			if (p in t) return t[p];
			return store.has(p) ? store.get(p) : undefined;
		},
		set: (t, p, v) => { store.set(String(p), String(v)); return true; },
		deleteProperty: (t, p) => { store.delete(String(p)); return true; },
		has: (t, p) => p in t || store.has(p),
	});
};

const endpointOf = (url) => {
	try {
		return new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
	} catch (e) {
		return String(url || '');
	}
};

const install = () => {
	// Storage: keep every write inside the frame.
	const local = shadowStorage(window.localStorage);
	const session = shadowStorage(window.sessionStorage);
	Object.defineProperty(window, 'localStorage', { configurable: true, get: () => local });
	Object.defineProperty(window, 'sessionStorage', { configurable: true, get: () => session });

	// Run as the fictional preview profile. profileName must match the profile fixtures.
	local.setItem('profileId', PREVIEW_PROFILE_ID);
	local.setItem('profileName', PREVIEW_PROFILE_NAME);
	local.setItem('masterLocationFilter', 'true');
	if (!local.getItem('language')) local.setItem('language', 'english');
	['wl_favicon', 'wl_appleTouchIcon', 'wl_openGraphImage', 'wl_twitterCardImage', 'wl_structuredDataLogo'].forEach((k) => local.removeItem(k));

	// Cookies: readable, never writable, so nothing in the frame can sign the user out.
	const cookieDesc = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie');
	if (cookieDesc && cookieDesc.get) {
		Object.defineProperty(document, 'cookie', { configurable: true, get: () => cookieDesc.get.call(document), set: () => { } });
	}

	// Analytics: no tracking from the preview.
	const noop = () => { };
	const analyticsStub = { page: noop, track: noop, identify: noop, reset: noop, group: noop, alias: noop, ready: noop };
	Object.defineProperty(window, 'analytics', { configurable: true, get: () => analyticsStub, set: () => { } });

	// API: every axios request is answered locally. The real adapter is never called.
	axios.defaults.adapter = (config) => {
		const data = fixtureFor(endpointOf(config.url), config);
		return Promise.resolve({ data, status: 200, statusText: 'OK', headers: {}, config, request: {} });
	};

	// fetch: translations get English page text; anything else gets an empty success.
	window.fetch = (input) => {
		const url = typeof input === 'string' ? input : (input && input.url) || '';
		const body = translationFor(url) || fixtureFor(endpointOf(url), {});
		return Promise.resolve(new Response(JSON.stringify(body), { status: 200, headers: { 'Content-Type': 'application/json' } }));
	};

	// Other channels: inert, so the frame can't open any connection.
	function InertSocket() {
		this.readyState = 3;
		this.send = noop;
		this.close = noop;
		this.addEventListener = noop;
		this.removeEventListener = noop;
	}
	window.WebSocket = InertSocket;
	window.EventSource = InertSocket;
	if (navigator.sendBeacon) navigator.sendBeacon = () => true;

	// Hide the app shell; the preview draws its own navigation around the page.
	document.documentElement.classList.add('lc-role-preview');
	const style = document.createElement('style');
	const rules = [
		'.lc-role-preview .main-wrapper__sidebar,',
		'.lc-role-preview .main-wrapper > aside:last-child { display: none !important; }',
		'.lc-role-preview .main-wrapper__content { margin-left: 0 !important; width: 100% !important; max-width: 100% !important; }',
		// Settings sidebar a little wider than the app's 230px.
		'.lc-role-preview .settings__sidebar:not(.collapsed) { min-width: 300px !important; max-width: 260px !important; }',
	];
	// Settings sidebar: hide links to settings pages the previewed role can't open. Some entries share
	// a switch with another page (Billing / Manage Users, Filter Composer / Profile Settings), so the
	// switches alone can't hide them one by one.
	settingsRoutes.forEach((s) => {
		if (!settingsPageVisible(s.key)) {
			rules.push(`.lc-role-preview .settings__sidebar--menu li:has(a[href="${s.route}"]) { display: none !important; }`);
		}
	});
	style.textContent = rules.join('\n');
	(document.head || document.documentElement).appendChild(style);

	// Navigation between preview pages happens inside this frame's router (no reload, no requests).
	// The parent moves the frame with __lcPreviewNavigate; moves made here (e.g. the Settings sidebar)
	// are reported back so the parent's menu stays in sync.
	window.__lcPreviewNavigate = (path) => {
		if (history.location.pathname !== path) history.push(path);
	};
	history.listen((location) => {
		try {
			if (window.parent.__lcPreviewOnNavigate) window.parent.__lcPreviewOnNavigate(location.pathname);
		} catch (e) { /* parent gone */ }
	});
};

if (isPreviewMode()) {
	install();
}
